import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Field, Input } from "../../components/ui";
import { useApiError } from "../../hooks/useApiError.js";
import {
  COLLECTION_NOTE_MAX,
  isoToLocalInput,
  localInputToIso,
  saveRequestCollection,
} from "./collectionApi";
import "./collection-form.scss";

/**
 * Collection / courier date-time + note for ONE request. Used in the request
 * page's Order Summary card (order, sell and PX alike) and in the Dashboard's
 * "Book collection" action. Saves through PUT /api/requests/{id}/collection.
 *
 * @param {number} requestId
 * @param {string|null} [initialAt]     ISO
 * @param {string|null} [initialNote]
 * @param {(saved: {collection_at, collection_note}) => void} [onSaved]
 * @param {() => void} [onCancel]        shows a Cancel button (modal use)
 */
export default function CollectionForm({ requestId, initialAt = null, initialNote = null, onSaved, onCancel }) {
  const { showApiError } = useApiError();
  const [at, setAt] = useState(isoToLocalInput(initialAt));
  const [note, setNote] = useState(initialNote || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(initialAt);

  useEffect(() => {
    setAt(isoToLocalInput(initialAt));
    setNote(initialNote || "");
    setSavedAt(initialAt);
  }, [requestId, initialAt, initialNote]);

  const save = async (clear = false) => {
    if (!requestId) return;
    setSaving(true);
    try {
      const collectionAt = clear ? null : localInputToIso(at);
      const data = await saveRequestCollection(requestId, { collectionAt, note: clear ? null : note });
      const saved = {
        collection_at: data?.collection_at ?? collectionAt,
        collection_note: data?.collection_note ?? (clear ? null : note || null),
      };
      if (clear) {
        setAt("");
        setNote("");
      }
      setSavedAt(saved.collection_at);
      onSaved?.(saved);
    } catch (err) {
      showApiError(err, "save the collection");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rc-collection-form">
      <Field label="Collection / courier">
        <Input type="datetime-local" size="md" fullWidth value={at} onChange={(e) => setAt(e.target.value)} />
      </Field>
      <Field label="Note" hint={`${note.length} / ${COLLECTION_NOTE_MAX}`}>
        <Input
          size="md"
          fullWidth
          value={note}
          maxLength={COLLECTION_NOTE_MAX}
          placeholder="e.g. DHL pickup, 2 boxes"
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      <ButtonGroup align="end">
        {onCancel ? (
          <Button variant="secondary" size="sm" data-btn="collection-cancel" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        {savedAt ? (
          <Button variant="ghost" size="sm" data-btn="collection-clear" disabled={saving} onClick={() => save(true)}>
            Clear
          </Button>
        ) : null}
        <Button variant="primary" size="sm" data-btn="collection-save" loading={saving} disabled={!at} onClick={() => save(false)}>
          Save collection
        </Button>
      </ButtonGroup>
    </div>
  );
}
